/**
 * lib/auth/interceptor.ts
 *
 * What it does:
 *   Installs the axios response interceptor that recovers transient Clerk
 *   session-token 401s: force-refresh via getToken({ skipCache: true }),
 *   dedupe concurrent refreshes, replay once, and branch fail-soft (timeout)
 *   vs fail-closed (confirmed signout).
 *
 * What it does NOT do:
 *   Does not create the axios instance — callers pass their singleton.
 *   Does not handle portal token auth or non-Clerk contexts.
 *
 * Depends on:
 *   - @/lib/auth/handle-unrecoverable-auth (terminal toast + redirect)
 *   - @/lib/auth/recovery-telemetry (Segment + Sentry signals)
 *   - @/lib/posthog/auth-recovery-flag (kill switch)
 */

import { handleUnrecoverableAuth } from "@/lib/auth/handle-unrecoverable-auth";
import { onAuthRecoveryFailed, onAuthRecoverySucceeded, onAuthRecoveryTimedOut } from "@/lib/auth/recovery-telemetry";
import { isAuthRecoveryEnabled } from "@/lib/posthog/auth-recovery-flag";

import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";

type AuthRetryRequestConfig = InternalAxiosRequestConfig & { _authRetry?: boolean };

type ClerkRefreshTimeoutError = Error & { isClerkRefreshTimeout: true };

function isClerkRefreshTimeout(error: unknown): error is ClerkRefreshTimeoutError {
  return (
    typeof error === "object" &&
    error !== null &&
    "isClerkRefreshTimeout" in error &&
    (error as { isClerkRefreshTimeout?: boolean }).isClerkRefreshTimeout === true
  );
}

function isAxiosErrorWithConfig(error: unknown): error is AxiosError & { config: AuthRetryRequestConfig } {
  return typeof error === "object" && error !== null && "config" in error && (error as AxiosError).config != null;
}

// Singleton dedup: concurrent 401s share one in-flight refresh promise.
let refreshInFlight: Promise<string | null> | null = null;

// A blocked/unreachable /tokens endpoint is a NETWORK error (not 4xx), so
// clerk-js retries with backoff ~8 times over ~3 minutes before rejecting.
// Awaiting that whole budget hangs every queued request behind one dead
// refresh. Race it against a short timeout so requests resolve in seconds.
const REFRESH_TIMEOUT_MS = 6000;

// A single timed-out refresh is NOT a confirmed signout — slow mobile
// networks and a congested FAPI legitimately blow the 6s budget. Those fail
// soft unless consecutive timeouts mean /tokens is genuinely dead.
const MAX_CONSECUTIVE_REFRESH_TIMEOUTS = 3;
let consecutiveRefreshTimeouts = 0;

function refreshClerkSessionToken(): Promise<string | null> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Clerk session is not available outside the browser"));
  }
  const session = window.Clerk?.session;
  if (!session?.getToken) {
    return Promise.reject(new Error("Clerk session is not available"));
  }
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      const timeoutError = new Error("Clerk token refresh timed out") as ClerkRefreshTimeoutError;
      timeoutError.isClerkRefreshTimeout = true;
      reject(timeoutError);
    }, REFRESH_TIMEOUT_MS);
  });
  return Promise.race([session.getToken({ skipCache: true }), timeout])
    .then(
      (token) => {
        consecutiveRefreshTimeouts = 0;
        return token;
      },
      (refreshError: unknown) => {
        if (isClerkRefreshTimeout(refreshError)) {
          consecutiveRefreshTimeouts += 1;
          onAuthRecoveryTimedOut({
            msWaited: REFRESH_TIMEOUT_MS,
            consecutiveTimeouts: consecutiveRefreshTimeouts,
          });
        }
        throw refreshError;
      }
    )
    .finally(() => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    });
}

export function installAuthRecoveryInterceptor(api: AxiosInstance): void {
  api.interceptors.response.use(
    (res) => res,
    async (error: unknown) => {
      if (!isAxiosErrorWithConfig(error)) {
        return Promise.reject(error);
      }

      const original = error.config;

      const isPortalRequest = typeof original.url === "string" && original.url.startsWith("/api/portal/");
      if (
        error.response?.status !== 401 ||
        original._authRetry ||
        typeof window === "undefined" ||
        isPortalRequest ||
        !window.Clerk?.session ||
        !isAuthRecoveryEnabled()
      ) {
        return Promise.reject(error);
      }

      original._authRetry = true;
      const start = Date.now();
      let refreshed = false;

      try {
        if (!refreshInFlight) {
          refreshInFlight = refreshClerkSessionToken().finally(() => {
            refreshInFlight = null;
          });
        }
        await refreshInFlight;
        refreshed = true;

        const retryResponse = await api(original);
        onAuthRecoverySucceeded({
          url: original.url,
          msToRecovery: Date.now() - start,
        });
        return retryResponse;
      } catch (refreshOrRetryError: unknown) {
        const replayStatus =
          typeof refreshOrRetryError === "object" &&
          refreshOrRetryError !== null &&
          "response" in refreshOrRetryError
            ? (refreshOrRetryError as AxiosError).response?.status
            : undefined;
        const isRefreshTimeout = !refreshed && isClerkRefreshTimeout(refreshOrRetryError);

        if (isRefreshTimeout && consecutiveRefreshTimeouts < MAX_CONSECUTIVE_REFRESH_TIMEOUTS) {
          return Promise.reject(error);
        }

        const isAuthFailure = !refreshed || replayStatus === 401 || !window.Clerk?.session;

        if (isAuthFailure) {
          onAuthRecoveryFailed({
            url: original.url,
            statusCode: 401,
            msToRecovery: Date.now() - start,
            cause: refreshOrRetryError,
          });
          handleUnrecoverableAuth({ reason: isRefreshTimeout ? "verification-timeout" : "expired" });
          return Promise.reject(error);
        }

        return Promise.reject(refreshOrRetryError);
      }
    }
  );
}
